import type { ProbeOutcome, ProbeResultRecord } from '@nadoutong/contracts';
import { useProbeResults } from '../api/useUpstreams.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { formatFineUsdCents } from './formatters.ts';
import { StatusChip } from './StatusChip.tsx';

/**
 * Probe history card — persisted probe outcomes for an upstream. Epic
 * #109 P2.13.
 *
 * Loads from `GET /v1/ops/upstreams/:id/probes` (gateway migration 0007's
 * `probe_results` table). Newest-first; no pagination — the worker caps
 * at 200 rows and the ops console scrolls the fixed window. React Query
 * key is invalidated by the probe mutation (`useProbeUpstream`) so a
 * fresh probe appears at the top without a manual refresh.
 *
 * Distinct from the in-flight "Recent probes" list shown directly below
 * the probe form: that one is ephemeral (resets on page unload) and
 * shows full `ProbeResponse` detail; this card is the persistent audit
 * trail across refreshes and operators.
 */

const OUTCOME_VARIANT: Record<ProbeOutcome, 'ok' | 'warning' | 'error' | 'neutral'> = {
  pass: 'ok',
  fail_shape: 'error',
  fail_content: 'error',
  fail_upstream_error: 'error',
  fail_timeout: 'warning',
};

const OUTCOME_LABEL_KEY: Record<ProbeOutcome, I18nKey> = {
  pass: 'probe.outcome.pass',
  fail_shape: 'probe.outcome.fail_shape',
  fail_content: 'probe.outcome.fail_content',
  fail_upstream_error: 'probe.outcome.fail_upstream_error',
  fail_timeout: 'probe.outcome.fail_timeout',
};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

interface Props {
  readonly upstreamId: string;
}

export function ProbeHistoryCard({ upstreamId }: Props): JSX.Element {
  const t = useT();
  const query = useProbeResults(upstreamId);

  return (
    <article
      data-testid="probe-history-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-baseline justify-between gap-3">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('upstream_detail.probe_history.title')}
        </h3>
        <span className="text-[10px] text-outline" data-testid="probe-history-count">
          {t('upstream_detail.probe_history.count', {
            count: query.data?.length ?? 0,
          })}
        </span>
      </header>
      <p className="text-sm text-outline">{t('upstream_detail.probe_history.body')}</p>
      {query.isLoading ? (
        <p className="text-xs text-outline" data-testid="probe-history-loading">
          {t('upstream_detail.probe_history.loading')}
        </p>
      ) : query.isError ? (
        <p
          className="text-xs text-error"
          role="alert"
          data-testid="probe-history-error"
        >
          {t('upstream_detail.probe_history.error')}
        </p>
      ) : (query.data ?? []).length === 0 ? (
        <p className="text-xs text-outline" data-testid="probe-history-empty">
          {t('upstream_detail.probe_history.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full text-left text-xs"
            data-testid="probe-history-table"
          >
            <thead>
              <tr className="border-b border-outline-variant text-[10px] uppercase tracking-[0.08em] text-outline">
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t('upstream_detail.probe_history.column.time')}
                </th>
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t('upstream_detail.probe_history.column.model')}
                </th>
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t('upstream_detail.probe_history.column.outcome')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  {t('upstream_detail.probe_history.column.latency')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  {t('upstream_detail.probe_history.column.cost')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  {t('upstream_detail.probe_history.column.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(query.data ?? []).map((row: ProbeResultRecord) => (
                <tr
                  key={row.id}
                  className="border-b border-outline-variant/40"
                  data-testid={`probe-history-row-${row.id}`}
                  data-outcome={row.outcome}
                >
                  <td className="py-2 pr-4 font-mono text-[11px] text-outline">
                    {formatTimestamp(row.created_at)}
                  </td>
                  <td className="py-2 pr-4 font-mono text-[11px] text-on-surface">
                    {row.vendor}/{row.model}
                  </td>
                  <td className="py-2 pr-4">
                    <StatusChip
                      variant={OUTCOME_VARIANT[row.outcome]}
                      label={t(OUTCOME_LABEL_KEY[row.outcome])}
                    />
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
                    {row.latency_ms}ms
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
                    {formatFineUsdCents(row.cost_usd_cents)}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-[11px] text-outline">
                    {row.upstream_status > 0 ? row.upstream_status : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}
